import React, { Fragment, useEffect, useRef, useState, useLayoutEffect } from "react"
import { Dialog, Transition } from '@headlessui/react'
import { motion } from "framer-motion"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { springVariants, rightVariants, leftVariants, springViewport } from '../utils/framerVariants'

const ContentTeaser = ({ data }) => {

  const hasSpacing = data.TeaserMargins && data.TeaserMargins.startsWith("None")
  const isOpposite = data.TeaserDirection && data.TeaserDirection.startsWith("Opposite")

  // opener and closer for modalbox
  const [open, setOpen] = useState(false)
  const cancelButtonRef = useRef(null)

  // check if we have to deal with a video
  const isVideo = data.Media && data.Media.mime.startsWith("video")

  return (
    <>
    {isOpposite ? (
      <article className={`contentTeaser mx-auto max-w-none 4xl:max-w-10xl relative overflow-hidden transition-color duration-200 ${hasSpacing ? "" : "mb-16 md:mb-24 lg:mb-32 xl:mb-48"}`}>
        <div className="teaser-link cursor-pointer" onClick={() => setOpen(true)}>
          <motion.div className="relative z-[1] overflow-hidden md:absolute md:right-0 md:h-full md:w-1/3 lg:w-1/2"
            variants={rightVariants} initial="invisible" whileInView="visible" viewport={springViewport}>
            {isVideo ? (
              <>
                {data.Media &&
                  <video className="h-full w-full object-cover" playsInline autoPlay loop mute="true" muted>
                    <source src={data.Media.file.url} type={data.Media.mime} />
                  </video>
                }
              </>
              ) : (
              <>
                {data.Media && <GatsbyImage
                image={getImage(data.Media.file)}
                alt={data.Media.alternativeText || "Alternative Text missing"}
                className="h-full w-full object-cover"
                />
              }
             </>
            )}
          </motion.div>
          <motion.div
            variants={leftVariants} initial="invisible" whileInView="visible" viewport={springViewport}
            className="relative mx-auto py-12 md:py-24 lg:py-32 xl:py-40">
            <div className="pl-8 pr-8 md:mr-auto md:w-2/3 md:pl-16 md:pr-16 lg:w-1/2 lg:pl-24 lg:pr-24 xl:pl-32 xl:pr-32">
              {data.Subheadline && <p className="text-text leading-text tracking-text font-normal mb-4">{data.Subheadline}</p>}
              {data.Headline && <motion.div
                  variants={springVariants}
                  initial="invisible"
                  whileInView="visible"
                  viewport={springViewport}
                  className="mb-8"
                  dangerouslySetInnerHTML={{
                    __html: data.Headline,
                  }}
                />
              }
              {data.Text && <motion.div
                variants={springVariants}
                initial="invisible"
                whileInView="visible"
                viewport={springViewport}
                dangerouslySetInnerHTML={{
                  __html: data.Text,
                }}
              />
              }
            </div>
          </motion.div>
        </div>
      </article>
      ) : (
      <article className={`contentTeaser mx-auto max-w-none 4xl:max-w-10xl relative overflow-hidden transition-color duration-200 ${hasSpacing ? "" : "mb-16 md:mb-24 lg:mb-32 xl:mb-48"}`}>
        <div className="teaser-link cursor-pointer" onClick={() => setOpen(true)}>
          <motion.div className="relative z-[1] overflow-hidden md:absolute md:left-0 md:h-full md:w-1/2 lg:w-1/2"
            variants={leftVariants} initial="invisible" whileInView="visible" viewport={springViewport}>
            {isVideo ? (
              <>
                {data.Media &&
                  <video className="h-full w-full object-cover" playsInline autoPlay loop mute="true" muted>
                    <source src={data.Media.file.url} type={data.Media.mime} />
                  </video>
                }
              </>
              ) : (
              <>
                {data.Media && <GatsbyImage
                image={getImage(data.Media.file)}
                alt={data.Media.alternativeText || "Alternative Text missing"}
                className="w-full h-full object-cover"
                />
              }
             </>
            )}
          </motion.div>
          <motion.div
            variants={rightVariants} initial="invisible" whileInView="visible" viewport={springViewport}
            className="relative mx-auto py-12 md:py-24 lg:py-32 xl:py-40">
            <div className="pl-8 pr-8 md:ml-auto md:w-1/2 md:pl-16 md:pr-16 lg:w-1/2 lg:pl-24 lg:pr-24 xl:pl-32 xl:pr-32">
              {data.Subheadline && <p className="text-text leading-text tracking-text font-normal mb-4">{data.Subheadline}</p>}
              {data.Headline && <motion.div
                  variants={springVariants}
                  initial="invisible"
                  whileInView="visible"
                  viewport={springViewport}
                  className="mb-8"
                  dangerouslySetInnerHTML={{
                    __html: data.Headline,
                  }}
                />
              }
              {data.Text && <motion.div
                variants={springVariants}
                initial="invisible"
                whileInView="visible"
                viewport={springViewport}
                dangerouslySetInnerHTML={{
                  __html: data.Text,
                }}
              />
              }
            </div>
          </motion.div>
        </div>
      </article>
    )}

    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[100] modalbox" initialFocus={cancelButtonRef} onClose={setOpen}>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black" />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed right-0 pr-2 pt-2 top-0 md:pr-8 md:pt-8 block z-[100]">
            <button
              type="button"
              className="text-black hover:text-black/80 md:text-white md:hover:text-white hover:scale-[1.1] transition-transform duration-200 focus:outline-none"
              onClick={() => setOpen(false)}
            >
              <span className="sr-only">Close</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-8 w-8 2xl:h-12 2xl:w-12">
                <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center cursor-pointer">

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="my-0 mx-0 md:my-16 md:mx-0 transform overflow-hidden bg-white text-left transition-all max-w-3xl sm:w-full cursor-default relative">
                  <div
                    className="modalbox-richtext text-text tracking-text leading-text pl-8 md:pl-16 lg:pl-16 pr-12 md:pr-24 lg:pr-24 py-8 md:py-16 lg:py-16"
                    dangerouslySetInnerHTML={{
                      __html: data.ModalboxContent,
                    }}
                  />
                  {data.ModalboxImage ? (
                    <GatsbyImage
                      image={getImage(data.ModalboxImage.file)}
                      alt={data.ModalboxImage.alternativeText || "Alternative Text missing"}
                    />
                  ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>

    </>
  )
}

export default ContentTeaser
